
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const PoliticaCancelamentoPage = () => {
  const [activeSection, setActiveSection] = useState('visao-geral');

  const sections = [
    { id: 'visao-geral', label: 'Visão Geral' },
    { id: 'prazos', label: 'Prazos e Penalizações' },
    { id: 'reembolsos', label: 'Reembolsos' },
    { id: 'alteracoes', label: 'Alterações de Reserva' },
    { id: 'forca-maior', label: 'Força Maior' },
    { id: 'no-show', label: 'No-Show' },
    { id: 'como-cancelar', label: 'Como Cancelar' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      window.scrollTo({ top: element.offsetTop - offset, behavior: 'smooth' });
    }
  };

  const cancelationTiers = [
    {
      icon: 'ri-checkbox-circle-line',
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      badge: 'bg-emerald-100 text-emerald-700',
      label: 'Cancelamento Gratuito',
      timing: 'Mais de 14 dias antes do check-in',
      refund: '100% reembolsado',
      description: 'Cancelamento sem qualquer custo. O valor total pago é devolvido na íntegra no prazo de 10 dias úteis.',
    },
    {
      icon: 'ri-error-warning-line',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      badge: 'bg-amber-100 text-amber-700',
      label: 'Cancelamento Parcial',
      timing: 'Entre 7 e 14 dias antes do check-in',
      refund: '50% reembolsado',
      description: 'Penalização de 50% do valor total da reserva. O restante é reembolsado no prazo de 10 dias úteis.',
    },
    {
      icon: 'ri-close-circle-line',
      color: 'text-rose-600',
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      badge: 'bg-rose-100 text-rose-700',
      label: 'Sem Reembolso',
      timing: 'Menos de 7 dias antes do check-in',
      refund: '0% reembolsado',
      description: 'O valor total da reserva é retido. Não há direito a reembolso, exceto em casos de força maior devidamente comprovados.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white pt-32 pb-24">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3761eaa90e4bc9949c7d59f374d3b2c6.jpg"
            alt="Madeira coastline"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/50 to-slate-900/80"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-sea-blue/20 border-2 border-sea-blue">
              <i className="ri-calendar-close-line text-3xl text-sea-blue"></i>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            Política de Cancelamento
          </h1>

          <p className="text-lg text-slate-300 max-w-3xl mx-auto mb-10">
            Queremos que a sua experiência seja tranquila do início ao fim. Conheça as nossas condições de cancelamento, reembolso e alteração de reservas de forma clara e transparente.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
            <div className="flex items-center space-x-2">
              <i className="ri-calendar-line text-sea-blue"></i>
              <span>Última atualização: 15 de janeiro de 2025</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-shield-check-line text-sea-blue"></i>
              <span>Política transparente e justa</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-time-line text-sea-blue"></i>
              <span>Reembolsos em até 10 dias úteis</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Nav */}
      <div className="sticky top-20 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center space-x-1 overflow-x-auto py-4 scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeSection === section.id
                    ? 'bg-sea-blue text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">

        {/* Visão Geral */}
        <section id="visao-geral" className="mb-16 scroll-mt-36">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-eye-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">1. Visão Geral</h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed mb-8">
            <p>
              <strong>1.1 Compromisso com a Transparência:</strong> Na Seaview &amp; Levada Escape, acreditamos que uma política de cancelamento clara e justa é fundamental para uma relação de confiança com os nossos hóspedes. Esta política aplica-se a todas as reservas diretas efetuadas através do nosso website, WhatsApp ou contacto telefónico.
            </p>
            <p>
              <strong>1.2 Reservas em Plataformas Externas:</strong> Para reservas efetuadas através de plataformas como Booking.com, Airbnb ou Expedia, aplicam-se as políticas de cancelamento específicas de cada plataforma, que podem diferir das condições aqui descritas.
            </p>
            <p>
              <strong>1.3 Confirmação da Reserva:</strong> A reserva é considerada confirmada após o pagamento do sinal ou do valor total. A partir desse momento, esta política de cancelamento entra em vigor.
            </p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {cancelationTiers.map((tier) => (
              <div
                key={tier.label}
                className={`rounded-xl border-2 ${tier.border} ${tier.bg} p-5 flex flex-col space-y-3`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${tier.bg}`}>
                  <i className={`${tier.icon} text-2xl ${tier.color}`}></i>
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full w-fit ${tier.badge}`}>
                  {tier.refund}
                </span>
                <h3 className={`font-bold text-base ${tier.color}`}>{tier.label}</h3>
                <p className="text-xs text-slate-500 font-medium">{tier.timing}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{tier.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Prazos e Penalizações */}
        <section id="prazos" className="mb-16 scroll-mt-36">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-timer-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">2. Prazos e Penalizações</h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative pl-8 border-l-2 border-slate-200 space-y-8 mb-8">
            {[
              {
                dot: 'bg-emerald-500',
                title: 'Mais de 14 dias antes do check-in',
                tag: '100% Reembolso',
                tagColor: 'bg-emerald-100 text-emerald-700',
                text: 'Cancelamento totalmente gratuito. O valor integral pago é devolvido sem qualquer dedução. O reembolso é processado no prazo de 10 dias úteis para a conta de origem do pagamento.',
              },
              {
                dot: 'bg-amber-500',
                title: 'Entre 7 e 14 dias antes do check-in',
                tag: '50% Reembolso',
                tagColor: 'bg-amber-100 text-amber-700',
                text: 'Penalização de 50% do valor total da reserva. O valor restante (50%) é reembolsado no prazo de 10 dias úteis. O sinal de reserva pode ser retido como parte desta penalização.',
              },
              {
                dot: 'bg-orange-500',
                title: 'Entre 3 e 7 dias antes do check-in',
                tag: '25% Reembolso',
                tagColor: 'bg-orange-100 text-orange-700',
                text: 'Penalização de 75% do valor total. Apenas 25% do valor pago é reembolsado. Recomendamos vivamente a contratação de um seguro de viagem para cobrir imprevistos neste período.',
              },
              {
                dot: 'bg-rose-500',
                title: 'Menos de 3 dias antes do check-in',
                tag: '0% Reembolso',
                tagColor: 'bg-rose-100 text-rose-700',
                text: 'Sem direito a reembolso. O valor total da reserva é retido. Exceções apenas em casos de força maior devidamente documentados (ver secção 5).',
              },
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className={`absolute -left-10 top-1 w-4 h-4 rounded-full border-2 border-white shadow ${item.dot}`}></div>
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h3 className="font-bold text-slate-900 text-sm">{item.title}</h3>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${item.tagColor}`}>{item.tag}</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>2.5 Cálculo do Prazo:</strong> O prazo de cancelamento é calculado em dias corridos (não úteis), contados a partir da data e hora em que o pedido de cancelamento é recebido por escrito (email ou WhatsApp), até à data de check-in prevista.
            </p>
            <p>
              <strong>2.6 Épocas Especiais:</strong> Durante o Natal, Ano Novo, Carnaval e Semana Santa, aplicam-se condições especiais: o cancelamento gratuito é possível até 21 dias antes do check-in. Entre 7 e 21 dias, a penalização é de 50%. Com menos de 7 dias, não há reembolso.
            </p>
          </div>
        </section>

        {/* Reembolsos */}
        <section id="reembolsos" className="mb-16 scroll-mt-36">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-refund-2-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">3. Reembolsos</h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>3.1 Prazo de Processamento:</strong> Os reembolsos são processados no prazo de 10 dias úteis após a confirmação do cancelamento. O tempo de crédito na conta do hóspede pode variar consoante o banco ou método de pagamento utilizado.
            </p>
            <p>
              <strong>3.2 Método de Reembolso:</strong> O reembolso é sempre efetuado pelo mesmo método de pagamento utilizado na reserva. Transferência bancária para transferências bancárias, MBWay para pagamentos MBWay, e crédito no cartão para pagamentos por cartão.
            </p>
            <p>
              <strong>3.3 Taxas de Processamento:</strong> Não cobramos taxas de processamento de cancelamento. O reembolso corresponde exatamente ao valor calculado segundo os prazos indicados, sem deduções adicionais.
            </p>
            <p>
              <strong>3.4 Taxa Turística:</strong> A taxa turística (€2 por pessoa/noite) é sempre reembolsada na totalidade em caso de cancelamento, independentemente do prazo, uma vez que não foi ainda prestado o serviço de alojamento.
            </p>
            <p>
              <strong>3.5 Crédito para Futura Reserva:</strong> Em alternativa ao reembolso monetário, o hóspede pode optar por receber um crédito equivalente para utilizar numa futura reserva, válido por 12 meses. Esta opção está disponível em qualquer prazo de cancelamento.
            </p>
            <p>
              <strong>3.6 Confirmação de Reembolso:</strong> Após o processamento, o hóspede receberá um email de confirmação com os detalhes do reembolso efetuado.
            </p>
          </div>
        </section>

        {/* Alterações de Reserva */}
        <section id="alteracoes" className="mb-16 scroll-mt-36">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-edit-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">4. Alterações de Reserva</h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>4.1 Pedidos de Alteração:</strong> Alterações de datas, tipo de apartamento ou número de hóspedes devem ser solicitadas por escrito (email ou WhatsApp) com pelo menos 7 dias de antecedência em relação à data de check-in original.
            </p>
            <p>
              <strong>4.2 Sujeito a Disponibilidade:</strong> Todas as alterações estão sujeitas a disponibilidade. Não garantimos a possibilidade de alterar a reserva, especialmente em períodos de alta ocupação.
            </p>
            <p>
              <strong>4.3 Ajuste de Preço:</strong> Alterações de datas podem implicar ajuste de preço, para mais ou para menos, consoante as tarifas vigentes para o novo período. O hóspede será informado do novo valor antes de confirmar a alteração.
            </p>
            <p>
              <strong>4.4 Redução do Período:</strong> A redução do número de noites de uma reserva já confirmada é tratada como cancelamento parcial e sujeita às penalizações correspondentes ao prazo de antecedência.
            </p>
            <p>
              <strong>4.5 Mudança de Apartamento:</strong> Pedidos de mudança para um apartamento diferente estão sujeitos a disponibilidade e possível diferença de preço. A mudança para um apartamento de valor inferior implica reembolso da diferença; para um de valor superior, pagamento adicional.
            </p>
            <p>
              <strong>4.6 Alterações de Emergência:</strong> Em situações de emergência comprovada, analisamos cada caso individualmente e tentamos encontrar a solução mais adequada para ambas as partes.
            </p>
          </div>
        </section>

        {/* Força Maior */}
        <section id="forca-maior" className="mb-16 scroll-mt-36">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-thunderstorms-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">5. Força Maior e Circunstâncias Excecionais</h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed mb-6">
            <p>
              <strong>5.1 Definição:</strong> Consideramos circunstâncias de força maior situações imprevisíveis e fora do controlo do hóspede que impossibilitem a realização da viagem, nomeadamente:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {[
              { icon: 'ri-heart-pulse-line', text: 'Doença grave ou hospitalização do hóspede ou familiar direto (comprovada por atestado médico)' },
              { icon: 'ri-user-heart-line', text: 'Falecimento de familiar direto (cônjuge, filhos, pais, irmãos)' },
              { icon: 'ri-flight-takeoff-line', text: 'Cancelamento de voo pela companhia aérea sem alternativa viável' },
              { icon: 'ri-global-line', text: 'Catástrofes naturais, pandemias declaradas ou restrições governamentais de viagem' },
              { icon: 'ri-alert-line', text: 'Alerta de viagem emitido pelo governo do país de origem do hóspede' },
              { icon: 'ri-building-line', text: 'Problemas graves no apartamento que o tornem inabitável (avaria estrutural, inundação, etc.)' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3 bg-slate-50 rounded-lg p-4 border border-slate-100">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-sea-blue text-lg`}></i>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>5.2 Documentação Necessária:</strong> Para invocar força maior, o hóspede deve apresentar documentação comprovativa no prazo de 48 horas após o cancelamento: atestado médico, certidão de óbito, comprovativo de cancelamento de voo, ou documento oficial equivalente.
            </p>
            <p>
              <strong>5.3 Análise Individual:</strong> Cada situação de força maior é analisada individualmente. Podemos oferecer reembolso total, crédito para futura reserva, ou reagendamento sem penalização, consoante as circunstâncias.
            </p>
            <p>
              <strong>5.4 Força Maior da Nossa Parte:</strong> Se, por razões de força maior da nossa parte (incêndio, inundação, obras de emergência), não pudermos disponibilizar o apartamento reservado, o hóspede receberá reembolso total ou, se preferir, alojamento alternativo de qualidade equivalente ou superior.
            </p>
          </div>
        </section>

        {/* No-Show */}
        <section id="no-show" className="mb-16 scroll-mt-36">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-user-unfollow-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">6. No-Show (Não Comparência)</h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>6.1 Definição de No-Show:</strong> Considera-se no-show quando o hóspede não se apresenta no apartamento na data de check-in prevista sem qualquer comunicação prévia, ou quando chega após as 23h59 do dia de check-in sem aviso.
            </p>
            <p>
              <strong>6.2 Consequências:</strong> Em caso de no-show, o valor total da reserva é retido sem direito a reembolso. O apartamento pode ser disponibilizado para outras reservas a partir das 12h00 do dia seguinte ao check-in previsto.
            </p>
            <p>
              <strong>6.3 Atraso no Check-in:</strong> Se o hóspede previr um atraso significativo, deve contactar-nos com a maior antecedência possível. Faremos todos os esforços para acomodar chegadas tardias, mas não podemos garantir disponibilidade após as 23h00 sem acordo prévio.
            </p>
            <p>
              <strong>6.4 Chegada Antecipada:</strong> Chegadas antes das 15h00 estão sujeitas a disponibilidade. Recomendamos contacto prévio para verificar possibilidade de early check-in. Pode ser aplicada uma taxa adicional de €20.
            </p>
          </div>
        </section>

        {/* Como Cancelar */}
        <section id="como-cancelar" className="mb-16 scroll-mt-36">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-guide-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">7. Como Cancelar a Sua Reserva</h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {[
              {
                step: '01',
                icon: 'ri-whatsapp-line',
                title: 'Via WhatsApp',
                desc: 'Envie uma mensagem para o nosso WhatsApp com o número da reserva, nome do hóspede e data de check-in. É o método mais rápido.',
                color: 'text-emerald-600',
                bg: 'bg-emerald-50',
              },
              {
                step: '02',
                icon: 'ri-mail-send-line',
                title: 'Via Email',
                desc: 'Envie um email para reservas@seaviewlevada.pt com o assunto "Cancelamento de Reserva" e os dados da sua reserva.',
                color: 'text-sea-blue',
                bg: 'bg-sea-blue/5',
              },
              {
                step: '03',
                icon: 'ri-phone-line',
                title: 'Por Telefone',
                desc: 'Ligue para +351 962 050 526 nos dias úteis entre as 9h00 e as 20h00. Confirmaremos o cancelamento por escrito.',
                color: 'text-slate-600',
                bg: 'bg-slate-50',
              },
            ].map((item) => (
              <div key={item.step} className={`rounded-xl p-6 border border-slate-100 ${item.bg}`}>
                <div className="flex items-center space-x-3 mb-4">
                  <span className="text-3xl font-black text-slate-200">{item.step}</span>
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-sm`}>
                    <i className={`${item.icon} text-xl ${item.color}`}></i>
                  </div>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>7.1 Confirmação de Cancelamento:</strong> O cancelamento só é válido após receção de confirmação escrita da nossa parte. Guarde sempre a confirmação de cancelamento para referência futura.
            </p>
            <p>
              <strong>7.2 Horário de Atendimento:</strong> Respondemos a pedidos de cancelamento nos dias úteis entre as 9h00 e as 20h00. Pedidos recebidos fora deste horário serão processados no próximo dia útil, mas a data e hora do pedido original serão consideradas para efeitos de prazo.
            </p>
            <p>
              <strong>7.3 Reservas em Plataformas:</strong> Para cancelar reservas efetuadas através de Booking.com, Airbnb ou outras plataformas, o cancelamento deve ser feito diretamente na plataforma onde a reserva foi realizada, seguindo as suas políticas específicas.
            </p>
          </div>
        </section>

        {/* CTA */}
        <div className="bg-gradient-to-br from-sea-blue to-teal-600 rounded-2xl p-8 md:p-12 text-white text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/20 mx-auto mb-6">
            <i className="ri-customer-service-2-line text-3xl"></i>
          </div>

          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Precisa de Ajuda com a Sua Reserva?
          </h3>

          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            A nossa equipa está disponível para esclarecer qualquer dúvida sobre cancelamentos, alterações ou reembolsos. Contacte-nos diretamente.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/351962050526?text=Olá!%20Preciso%20de%20ajuda%20com%20a%20minha%20reserva."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-white text-sea-blue px-8 py-4 rounded-lg font-semibold hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              <span>WhatsApp Direto</span>
            </a>

            <Link
              to="/contactos"
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line text-xl"></i>
              <span>Enviar Email</span>
            </Link>

            <Link
              to="/faq"
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-question-line text-xl"></i>
              <span>Ver FAQ</span>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PoliticaCancelamentoPage;
